import { seasonRepository } from "../storage/storage";
import PersistenceError from "./exceptions/persistence-error";
import SportService from "./sport-service";
import Season from "../sports/common/season";
import GamedaySeasonSettingsProvider from "./providers/gameday-season-settings-provider";
import {
    migrateClockConfig,
    normalizeFieldConfig,
    validateFieldConfig
} from "../sports/football/utils/field-config-utils";

class SeasonService {
    constructor() {
        this.settingsProvider = new GamedaySeasonSettingsProvider();
        this._refreshPromises = new Map();
    }

    setSeasonSettingsProvider(provider) {
        this.settingsProvider = provider;
    }

    async getBySeasonId(seasonId) {
        return seasonRepository.where("seasonId").equals(seasonId).first();
    }

    async saveSeason(season) {
        season.id = await seasonRepository.put(season);
        return season;
    }

    async createSeason(season) {
        try {
            const seasonId = await seasonRepository.put(season);
            return seasonRepository.getById(seasonId);
        } catch (err) {
            throw new PersistenceError(err);
        }
    }

    async getGameConfigurationOrDefault(
        seasonId,
        ruleSetId,
        skipRemoteRefresh = false
    ) {
        const season = await seasonRepository.getById(seasonId);

        if (!skipRemoteRefresh) {
            await this.refreshFromRemote(season.seasonId);
        }

        const updatedSeason = skipRemoteRefresh
            ? season
            : await seasonRepository.getById(seasonId);
        // The season's saved config only applies to its own ruleset: when a
        // game runs a different ruleset, fall back to that ruleset's
        // defaults. Same ruleset keeps the season config (event > season >
        // defaults precedence).
        const seasonConfig = migrateClockConfig(
            ruleSetId && ruleSetId !== updatedSeason.ruleSetId
                ? null
                : updatedSeason.gameConfiguration
        );

        const defaults = SportService.resolveDefaultConfig(
            updatedSeason.sport.sportId,
            ruleSetId || updatedSeason.ruleSetId
        );
        // Spread saved values over the defaults so fields added to the
        // schema after the config was saved still show up on the form.
        const gameConfiguration = seasonConfig
            ? {
                  ...defaults,
                  ...seasonConfig,
                  general: { ...defaults.general, ...seasonConfig.general },
                  // Sports without a clock section (tennis) must not gain one.
                  ...(defaults.clock && {
                      clock: { ...defaults.clock, ...seasonConfig.clock }
                  })
              }
            : defaults;

        const customFormData = SportService.resolveCustomFormData(
            updatedSeason.sport.sportId,
            ruleSetId || updatedSeason.ruleSetId
        );

        return { gameConfiguration, customFormData };
    }

    async saveGameConfiguration(seasonId, gameConfiguration) {
        const normalized = normalizeFieldConfig(gameConfiguration);
        validateFieldConfig(normalized.general);

        const season = await seasonRepository.getById(seasonId);
        season.gameConfiguration = normalized;
        const settingsUpdatedAt = new Date().toISOString();

        await seasonRepository.update(season.id, {
            gameConfiguration: normalized,
            settingsUpdatedAt
        });
        season.settingsUpdatedAt = settingsUpdatedAt;

        await this._pushRemoteSettings(season);

        return this.getGameConfigurationOrDefault(seasonId);
    }

    async saveRuleSetId(seasonId, ruleSetId) {
        const season = await seasonRepository.getById(seasonId);
        season.ruleSetId = ruleSetId;
        const settingsUpdatedAt = new Date().toISOString();

        await seasonRepository.update(season.id, {
            ruleSetId,
            settingsUpdatedAt
        });
        season.settingsUpdatedAt = settingsUpdatedAt;

        await this._pushRemoteSettings(season);

        return season;
    }

    async restoreDefaultGameConfiguration(seasonId) {
        const season = await seasonRepository.getById(seasonId);
        return this.saveGameConfiguration(
            seasonId,
            SportService.resolveDefaultConfig(
                season.sport.sportId,
                season.ruleSetId
            )
        );
    }

    async saveOrUpdateSeasonByRemoteId(season) {
        const toUpdate = await seasonRepository
            .where({ seasonId: season.seasonId })
            .first();

        return this.saveSeason(Object.assign(toUpdate || new Season(), season));
    }

    async refreshFromRemote(externalSeasonId) {
        if (this._refreshPromises.has(externalSeasonId)) {
            return this._refreshPromises.get(externalSeasonId);
        }

        const promise = this._doRefreshFromRemote(externalSeasonId).finally(
            () => {
                this._refreshPromises.delete(externalSeasonId);
            }
        );

        this._refreshPromises.set(externalSeasonId, promise);
        return promise;
    }

    async _doRefreshFromRemote(externalSeasonId) {
        try {
            const remoteSettings = await this.settingsProvider.getSettings(
                externalSeasonId
            );

            if (remoteSettings) {
                const season = await seasonRepository
                    .where({ seasonId: externalSeasonId })
                    .first();

                if (season) {
                    const remoteIsNewer = this._areRemoteSettingsNewer(
                        season,
                        remoteSettings
                    );

                    if (remoteIsNewer) {
                        const changes = {
                            settingsUpdatedAt:
                                remoteSettings.updatedAt ||
                                new Date().toISOString()
                        };
                        if (remoteSettings.ruleSetId) {
                            changes.ruleSetId = remoteSettings.ruleSetId;
                        }
                        if (remoteSettings.gameConfiguration) {
                            changes.gameConfiguration =
                                remoteSettings.gameConfiguration;
                        }
                        await seasonRepository.update(season.id, changes);
                    }
                }
            }
        } catch (e) {
            // Best-effort: if remote fetch fails, continue with local data
            console.error(e);
        }
    }

    _areRemoteSettingsNewer(season, remoteSettings) {
        return (
            !season.settingsUpdatedAt ||
            !remoteSettings.updatedAt ||
            remoteSettings.updatedAt > season.settingsUpdatedAt
        );
    }

    async _pushRemoteSettings(season) {
        try {
            await this.settingsProvider.saveSettings(season.seasonId, {
                ruleSetId: season.ruleSetId,
                gameConfiguration: season.gameConfiguration,
                updatedAt: season.settingsUpdatedAt || new Date().toISOString()
            });
        } catch (e) {
            // Best-effort: if remote push fails, local data is already saved
        }
    }
}

export default new SeasonService();
